import {urlPrefixed} from '../../config';
import {trackMediaPanelSnapFromChartHotkey} from '../../services/analytics';
import {ColorPaletteOption} from '../../util/colors';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {buildSingleRunLink} from '../MultiRunWorkspace/SingleRunMode/buildSingleRunLink';
import {
  type GraphLegendLinkTarget,
  type GraphRunLinkTarget,
  type GraphTooltip,
} from './graphRenderProps';
import {type LinePlotSettings} from './LinePlotSettingsContext';

/** Host policy consumes scalar destinations and retains no line or run collections. */
export function getGraphTooltip(
  settings: Pick<
    LinePlotSettings,
    | 'colorRunNames'
    | 'displayFullRunName'
    | 'entityName'
    | 'getNamedWorkspaceSearch'
    | 'hasMediaPanel'
    | 'highlightedCompanionRunOnly'
    | 'isSingleRun'
    | 'onSnapMediaPanelsToStep'
    | 'projectName'
    | 'showXRange'
    | 'singleRunNavParams'
    | 'tooltipNumberOfRuns'
    | 'xAxis'
  > &
    Partial<Pick<LinePlotSettings, 'colorPaletteOption' | 'xAxisFormat'>>,
  runNameTruncationType?: TruncationType
): GraphTooltip {
  function resolveRunLink(target: GraphRunLinkTarget) {
    if (settings.isSingleRun || target.isGrouped) {
      return undefined;
    }
    return buildSingleRunLink(
      settings.singleRunNavParams,
      {
        entityName: target.entityName ?? settings.entityName,
        projectName: target.projectName ?? settings.projectName,
        runName: target.runName,
      },
      settings.getNamedWorkspaceSearch()
    );
  }
  function activateRunLink(href: string) {
    window.open(urlPrefixed(href), '_blank', 'noopener,noreferrer');
  }
  function snapToX(x: number) {
    trackMediaPanelSnapFromChartHotkey({xAxis: settings.xAxis});
    settings.onSnapMediaPanelsToStep(x, settings.xAxis);
  }
  return {
    colorPalette: settings.colorPaletteOption ?? ColorPaletteOption.DEFAULT,
    colorRunNames: settings.colorRunNames,
    showFullRunName: settings.displayFullRunName,
    highlightedCompanionRunOnly: settings.highlightedCompanionRunOnly,
    runVisibility: settings.tooltipNumberOfRuns,
    runNameTruncation: runNameTruncationType,
    showXRange: settings.showXRange,
    resolveRunLink,
    onActivateRunLink: activateRunLink,
    onSnapToX: snapToX,
    showSnapToXHint: settings.hasMediaPanel,
    xValueFormat: settings.xAxisFormat,
  };
}

/** The prepared legend destination has its own scope, independent of crosshair navigation. */
export function getLegendRunLink(
  target: GraphLegendLinkTarget
): string | undefined {
  return target.legendHref;
}
